import { Component, ElementRef, Input, SimpleChanges, ViewChild } from '@angular/core';
import { CommentService } from 'src/app/services/comment/comment.service';
import { v5 as uuidv5 } from 'uuid';
import { environment } from '@env/environment';
import { Subscription } from 'rxjs';
import { ActivityHistoryService } from 'src/app/services/activity-history/activity-history.service';

@Component({
    selector: 'app-history',
    templateUrl: './history.component.html',
    styleUrls: ['./history.component.scss']
})
export class HistoryComponent {
    @Input() id!: string;

    @ViewChild('scrollContainer') scrollContainer!: ElementRef;

    histories: any[] = [];
    baseUrl = environment.BASE_PATH;
    pageNum: number = 0;
    pageSize: number = 10;
    isLoading: boolean = false;
    allLoaded: boolean = false;
    currentLengthMap: Map<string, number> = new Map();

    private subscription = new Subscription();

    constructor(
        private commentService: CommentService,
        private activityHistoryService: ActivityHistoryService
    ) { }

    ngOnInit() {
        this.subscription.add(
            this.activityHistoryService.historyCreated$.subscribe((data) => {
                const newItem = this.getHistoryDisplay(data);
                this.histories.unshift(newItem);
                if (this.histories.length > this.pageNum * this.pageSize) {
                    this.histories.pop();
                }
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['id'] && changes['id'].currentValue) {
            this.pageNum = 0;
            this.histories = [];
            this.currentLengthMap.clear();
            this.fetchData();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    fetchData() {
        if (this.isLoading || this.allLoaded) return;
        this.isLoading = true;

        const NAMESPACE = uuidv5.DNS;
        const historyId = uuidv5(`${this.id}-history`, NAMESPACE);

        this.commentService.list(historyId, this.pageNum, this.pageSize)
            .subscribe({
                next: (res) => {
                    if (!res.data?.length) this.allLoaded = true;
                    const newItems = res.data.map((history: any) => this.getHistoryDisplay(history));
                    this.histories = [...this.histories, ...newItems];
                    this.pageNum++;
                },
                complete: () => {
                    this.isLoading = false;
                },
            });
    }

    getHistoryDisplay(item: any) {
        const { content, created_by, created_at, id } = item;
        let parsedContent: any;
        try {
            parsedContent = JSON.parse(content);
        } catch {
            return null;
        }

        return {
            id: id,
            type: parsedContent?.type,
            action: parsedContent?.action || '',
            module: parsedContent?.module || '',
            detail: parsedContent?.detail || '',
            mode: parsedContent?.mode,
            user: created_by,
            created_at: created_at
        };
    }

    replaceWithDefaultImage(event: any): void {
        event.target.src = 'assets/images/default_user_avatar.png';
    }

    getType(target: any): string {
        return typeof target;
    }

    onScroll() {
        const container = this.scrollContainer.nativeElement;
        const scrollPosition = container.scrollTop + container.clientHeight;
        const scrollHeight = container.scrollHeight;

        if (scrollPosition >= scrollHeight - 10 && !this.isLoading && !this.allLoaded) {
            this.fetchData();
        }
    }

    getVisibleText(id: string, text: string): string {
        const end = this.currentLengthMap.get(id) ?? this.getIndexEndLength(text);
        const visibleText = text.slice(0, end);
        return end < text.length ? visibleText + '...' : visibleText;
    }

    getIndexEndLength(text: string): number {
        const lines = text.split('\n');
        let charCount = 0;
        let lineCount = 0;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            charCount += line.length + 1;
            lineCount++;
            if (lineCount >= 6 || charCount >= 500) break;
        }
        return Math.min(charCount, 500, text.length);
    }

    expandMore(id: string, text: string): void {
        const current = this.currentLengthMap.get(id) ?? this.getIndexEndLength(text);

        const lines = text.slice(current).split('\n');
        let additionalChars = 0;
        let lineCount = 0;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            additionalChars += line.length + 1;
            lineCount++;
            if (lineCount >= 6 || additionalChars >= 500) break;
        }

        const newEnd = Math.min(current + additionalChars, text.length);
        this.currentLengthMap.set(id, newEnd);
    }

    showLess(id: string, text: string): void {
        this.currentLengthMap.set(id, this.getIndexEndLength(text));
    }

    isFullyExpanded(id: string, text: string): boolean {
        const visible = this.currentLengthMap.get(id) ?? this.getIndexEndLength(text);
        return visible >= text.length;
    }

    shouldShowToggle(text: string): boolean {
        return text.length > this.getIndexEndLength(text);
    }
}
